// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { setNestedObjectValues, useFormik } from "formik";
import lodash, { isUndefined } from "lodash";
import {
  Box,
  Typography,
  Grid,
  InputLabel,
  Button,
  CircularProgress,
  Checkbox,
  useMediaQuery,
  Tooltip,
  InputAdornment,
  Autocomplete,
  TextField,
  Modal,
  IconButton,
  MenuItem,
  Select,
  Link,
} from "@mui/material";
import { Country, State, City } from "country-state-city";
import ImageIcon from "@mui/icons-material/Image";
import { UploadFile, Close, Visibility } from "@mui/icons-material";
import DeletePink from "../../../../assets/images/Delete-pink.svg";

import addContractorModalStyles from "../addContractorModal.module.css";
import RequiredFieldsNote from "./_components/RequiredFieldsNote";
import { requiredLabelSx } from "./_components/requiredField";
import individualFormStyles from "../../contractor-signup/individual-form.module.css";
import { CustomInput } from "../../../../components/Custom/Input";
import { CustomDatePicker } from "../../../../components/Custom/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  addContractorEmailAction,
  addContractorStep1Action,
  addContractorStep1UpdateAction,
} from "../../../../store/actions/contractor";
import {
  ADD_CONTRACTOR_STEP1_FAILURE,
  ADD_CONTRACTOR_STEP1_REQUEST,
  ADD_CONTRACTOR_STEP1_SUCCESS,
  ADD_CONTRACTOR_STEP1_UPDATE_FAILURE,
  ADD_CONTRACTOR_STEP1_UPDATE_REQUEST,
  ADD_CONTRACTOR_STEP1_UPDATE_SUCCESS,
  SET_CONTRACTOR_MULTIPLE_FIELDS,
} from "../../../../store/constants/contractor";
import {
  getEmailSanitized,
  phoneNumberForApi,
} from "../../../../utils/methods/formatters";
import { ALERT_DIALOG } from "../../../../store/constants/auth";
import { getCssVariableHandler } from "../../../../utils/styles/getCssVariables";
import { NavLink } from "react-router-dom";
import AddressAutocomplete, {
  AddressFilterAutocomplete,
} from "../../../../components/Custom/react-google-maps/AddressAutoComplete";
import { useCallback } from "react";
import { CustomAirportInput } from "./_components/CustomAirportInput";
import { Add } from "@mui/icons-material";
import { Info } from "@mui/icons-material";
import { CustomSwitch } from "../../../../components/Custom/Switch";
import { CustomBusinessInput } from "./_components/CustomBusinessInput";
import { CONTRACTOR_TYPE_3RD_PARTY } from "../../../../utils/constants/nmaes";
import { BarToggle } from "../../../../components/Others/BarToggle";
import { alertDialogAciton } from "../../../../store/actions/auth";
import ImageCropper from "../../../../components/user/ImageCroppper";
import { apiRequest } from "../../../../services/apiService";
import { USER_ROLES } from "../../../../staticData/userRoles";
import { isUserRole, isActiveRole } from "../../../../utils/methods/storeFunc";
import { calculateAge } from "../../../../utils/methods/timeDiff";
import { StyledSelectForPO } from "../../../event-details/components/AllContractorListTab";
import { validateFileType } from "../../../../utils/methods/contract/contractMethods";
import InfoHover from "../../../../components/Tooltip/InfoHover";
import { WORKMARKET_CONTRACTOR_TYPES } from "../../../../utils/constants/configs";
import { toAirportIdsPayload } from "../../../../utils/methods/contractors/contractorUtils";
import { buildIncompleteFormMessage } from "../../../../utils/methods/forms/validationFeedback";
import GovernmentIdDocumentCard from "../../../../components/user/GovernmentIdDocumentCard";

/**
 * Labels used by the "incomplete profile" dialog, so the list the user reads matches
 * the labels printed above each input instead of the raw formik field names.
 */
const STEP1_FIELD_LABELS = {
  firstName: "Legal First Name",
  middleName: "Legal Middle Name",
  lastName: "Legal Last Name",
  preferredName: "Preferred Name",
  profileId: "Contractor ID",
  workBookId: "WorkMarket Id",
  contractorType: "Contractor Type",
  companyName: "Company Name",
  einNumber: "EIN",
  mobile: "Phone Number",
  email: "Email",
  mailingAddress: "Mailing Address",
  city: "City",
  state: "State",
  zipCode: "Zip Code",
  dateOfBirth: "Date of Birth",
  // `age` has no input of its own on the contractor view: it is derived from the date
  // of birth, so point the user at the field they can actually edit.
  age: "Date of Birth (age could not be calculated)",
  emergencyContactName: "Emergency Contact Name",
  emergencyContactNumber: "Emergency Contact Number",
  preferredAirportIds: "Preferred Local Airports",
  additionalAirportIds: "Additional Airports",
  thirdPartyContractorId: "Company",
  insuranceCertificate: "Certificate Of Insurance",
};

const AddContractorFormStep1 = ({
  currentStep,
  setCurrentStep,
  closeModalHandler,
  FormDataBox,
  uiController,
  isEditing,
  isViewing,
  successCallbackStep1,
  isThirdPartyParent,
}) => {
  // Refs start...
  const isComponentMounted = React.useRef({
    resettingCity: false,
    updatingForm: false,
  });
  const imageRef = React.useRef(null);
  const resumeRef = React.useRef(null);
  const coiRef = useRef(null);
  const isSaveAndClose = useRef(false);
  // Refs end...

  // Static data start...
  const countryCode = "US";

  const statesOptions = React.useMemo(() => {
    const country = Country.getCountryByCode(countryCode);
    return State.getStatesOfCountry(country.isoCode)?.map((state) => {
      return {
        label: state?.name,
        value: state?.isoCode,
      };
    });
  }, []);

  const dontRunOnMountInit = React.useMemo(() => {
    return {
      updatingForm: false,
      resettingCity: false,
    };
  }, []);
  const isContractor = useMemo(() => {
    return isActiveRole(USER_ROLES.CONTRACTOR);
  }, []);
  // Static data end...

  // media query hooks start...
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  // media query hooks end...

  // Redux hooks start...
  const dispatch = useDispatch();
  // Selectors start...
  const auth = useSelector((store) => store.auth);
  const contractor = useSelector((store) => store.contractor);
  const { links } = useSelector((state) => state?.contractorLink);

  // Selectors end...
  // Redux hooks end...

  const contractorTypesOptions = React.useMemo(() => {
    return [
      { label: "W2", value: "w2" },
      { label: "1099", value: "1099" },
      { label: "V.C.", value: "V.C." },
      ...(contractor?.addContractor?.step1?.previouslySubmittedForm
        ?.contractorType
        ? []
        : [
            { label: "OutSource", value: CONTRACTOR_TYPE_3RD_PARTY.OUTSOURCED },
            {
              label: "Third Party",
              value: CONTRACTOR_TYPE_3RD_PARTY.THIRD_PARTY,
            },
          ]),
    ];
  }, [
    contractor?.addContractor?.step1?.previouslySubmittedForm?.contractorType,
  ]);

  // State variables start...
  const [initialValues, setInitialValues] = React.useState({
    firstName: "",
    middleName: "",
    lastName: "",
    preferredName: "",
    mobile: "",
    email: "",
    mailingAddress: "",
    city: "",
    state: "",
    zipCode: "",
    dateOfBirth: "",
    age: "",
    emergencyContactName: "",
    emergencyContactNumber: "",
    contractorType: "",
    einNumber: "",
    companyName: "",
    profileId: "",
    thirdPartyContractorId: null,
    // preferredCities: [],
    // preferredStates: [],
    preferredAirportIds: [{ Id: {}, radius: 50 }],
    additionalAirportIds: [{ Id: {}, radius: 50 }],
    workBookId: "",
  });

  // const [isValuesSyncedOnce, setIsValuesSyncedOnce] = React.useState(false);

  const [dontRunOnMount, setDontRunOnMount] =
    React.useState(dontRunOnMountInit);

  const [changedFields, setChangedFields] = React.useState({
    firstName: false,
    middleName: false,
    lastName: false,
    preferredName: false,
    mobile: false,
    email: false,
    mailingAddress: false,
    city: false,
    state: false,
    zipCode: false,
    // einNumber: false,
    companyName: false,
  });
  const [agreedToTandC, setAgreedToTandC] = React.useState(true);
  const [is3rdParty, setIs3rdParty] = React.useState(false);
  const [profileImage, setProfileImage] = React.useState(null);
  const [thumbnail, setThumbnail] = React.useState(null);
  const [resume, setResume] = React.useState(null);
  const [coi, setCoi] = useState(null);
  const [tempProfileImage, setTempProfileImage] = React.useState({
    objectUrl: "",
    blob: "",
  });
  const [cropModal, setCropModal] = React.useState(false);
  const [resumeViewModal, setResumeViewModal] = React.useState(false);
  const [coiViewModal, setCoiViewModal] = useState(false);
  const [imageViewModal, setImageViewModal] = React.useState(false);
  const [fileUploadLoading, setFileUploadLoading] = React.useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  // State variables end...

  // Form schema...
  const commonSchema = Yup.object().shape({
    firstName: Yup.string()
      .trim()
      .max(100, "Legal First Name should be at most 100 characters!")
      .required("Legal First Name is required!"),
    middleName: Yup.string()
      .trim()
      .max(100, "Legal Middle Name should be at most 100 characters!"),
    lastName: Yup.string()
      .trim()
      .max(100, "Legal Last Name should at most 100 characters!")
      .required("Legal Last Name is required!"),
    preferredName: Yup.string()
      .trim()
      .max(100, "Preferred Name should be at most 100 characters!"),
    mobile: Yup.string()
      .matches(/^\d{3}-\d{3}-\d{4}$/, "Phone number must be of 10 digit")
      .required("Phone Number is required!"),
    email: Yup.string().trim().email().required("Email is required!"),
    mailingAddress: Yup.string()
      .max(255, "Mailing Address should at most 255 characters!")
      .when("$contractorType", {
        is: () => isContractor,
        then: (schema) => schema.required("Mailing Address is required!"),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    // .required("Mailing Address is required!")
    contractorType: Yup.string().when("$contractorType", {
      is: () => isContractor,
      then: (schema) => schema.notRequired().nullable(),
      otherwise: (schema) => schema.required("Contractor type is required"),
    }),
    city: Yup.string()
      .max(80, "City Name at most 80 characters!")
      // .required("City is required!")
      .test("select-valid-city", "Please select a valid city", function () {
        const city = this.parent.city;
        const state = this.parent.state;

        return !(
          state !== "Select state" &&
          (city === "Select city" || city === "Please select state first")
        );
      })
      .when("$contractorType", {
        is: () => isContractor,
        then: (schema) => schema.required("City is required"),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    state: Yup.string()
      .max(80, "State name at most 80 characters!")
      // .required("State is required!")
      .test("select-valid-state", "Please select a valid state", function () {
        const state = this.parent.state;

        return state !== "Select state";
      })
      .when("$contractorType", {
        is: () => isContractor,
        then: (schema) => schema.required("State is required"),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    zipCode: Yup.string()
      .max(15, "Zip code at most 15 characters!")
      .when("$contractorType", {
        is: () => isContractor,
        then: (schema) => schema.required("Zip Code is required"),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    // .required("Zipcode is required!")
    dateOfBirth: Yup.string()
      .when("$contractorType", {
        is: () => isContractor,
        then: (schema) => schema.required("Date Of Birth is required"),
        otherwise: (schema) => schema.notRequired().nullable(),
      })
      .test(
        "age-min-18",
        "You must be at least 18 years old.",
        function (value) {
          if (!value || value === "") return true;
          const today = new Date();
          const dob = new Date(value);
          if (isNaN(dob.getTime())) return true;
          const age = today.getFullYear() - dob.getFullYear();
          const m = today.getMonth() - dob.getMonth();
          const d = today.getDate() - dob.getDate();
          const realAge = m < 0 || (m === 0 && d < 0) ? age - 1 : age;
          return realAge >= 18;
        },
      ),
    age: Yup.number()
      .when("$contractorType", {
        is: () => isContractor,
        then: (schema) => schema.required("Age is required"),
        otherwise: (schema) => schema.notRequired().nullable(),
      })
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .typeError("Age must be a number")
      .positive("Age must be positive")
      .integer("Age must be a whole number")
      .test(
        "age-min-18",
        "You must be at least 18 years old.",
        function (value) {
          if (value === undefined || value === null || value === "")
            return true;
          return value >= 18;
        },
      ),
    companyName: Yup.string()
      .max(255, "Company Name should be at most 255 characters!")
      .when(["contractorType"], {
        is: (contractorType) => {
          return ["V.C.", CONTRACTOR_TYPE_3RD_PARTY.THIRD_PARTY]?.includes(
            contractorType,
          );
        },
        then: (schema) => schema?.required("Company Name is required"),
        otherwise: (schema) => schema?.notRequired(),
      }),
    emergencyContactName: Yup.string()
      .trim()
      .max(100, "Emergency Contact Name should be at most 100 characters!")
      .when("$contractorType", {
        is: () => isContractor,
        then: (schema) => schema.required("Emergency Contact Name is required"),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    emergencyContactNumber: Yup.string()
      .matches(/^\d{3}-\d{3}-\d{4}$/, "Phone number must be of 10 digit")
      .when("$contractorType", {
        is: () => isContractor,
        then: (schema) =>
          schema.required("Emergency Contact Number is required"),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    preferredAirportIds: Yup.array().of(Yup.object()),
    additionalAirportIds: Yup.array().of(Yup.object()),
    thirdPartyContractorId: Yup.string().nullable(),
    workBookId: Yup.string()
      .trim()
      .when("$contractorType", {
        is: (contractorType) =>
          [
            "w2",
            CONTRACTOR_TYPE_3RD_PARTY?.OUTSOURCED,
            CONTRACTOR_TYPE_3RD_PARTY?.THIRD_PARTY,
          ]?.includes(contractorType),
        then: (schema) => schema.notRequired().nullable(),
        otherwise: (schema) => schema?.notRequired().nullable(),
      }),
  });

  const formik = useFormik({
    initialValues,
    // validationSchema: formSchemaCompany,
    validationSchema: commonSchema,
    validationContext: {
      contractorType: contractor?.addContractor?.step1?.data?.contractorType,
    },
    onSubmit: async (values) => {
      const { files, ...valuesRest } = {
        ...values,
        is3rdParty,
        email: values?.email?.trim()?.toLowerCase(),
      };

      const payloadData = {
        ...valuesRest,
        mobile: phoneNumberForApi(values.mobile),
        emergencyContactNumber: phoneNumberForApi(
          values?.emergencyContactNumber,
        ),
        dateOfBirth: values.dateOfBirth,
        profileId: values.profileId?.trim() ? values.profileId : null,
        // state: isEditing
        //   ? values?.state
        //   : State.getStateByCodeAndCountry(values.state, countryCode).name,
      };
      const { preferredAirportIds, additionalAirportIds, ...payload } =
        payloadData;

      // Prepare regular JSON payload (files will be uploaded separately)
      const finalPayload = {
        ...payload,
        // ⚠️ Do NOT go back to `filter(item => item?.Id?._id)`: that only works with a
        // populated ref and, when the backend serves the raw ObjectId, it drops
        // EVERYTHING — the PUT goes out with `[]` and `$set` silently wipes the
        // contractor's airports.
        preferredAirportIds: toAirportIdsPayload(values?.preferredAirportIds),
        additionalAirportIds: toAirportIdsPayload(values?.additionalAirportIds),
        CreatedBy: auth?.userId,
      };

      if (["1099", "V.C."]?.includes(values?.contractorType) && sendEmail) {
        finalPayload["sendOnBoardingLink"] = true;
      }

      // Function to upload files separately
      const uploadFilesIfNeeded = async (userId) => {
        const hasFiles =
          (profileImage && typeof profileImage !== "string") ||
          (resume && typeof resume !== "string") ||
          (coi && typeof coi !== "string");

        if (!hasFiles) return;

        setFileUploadLoading(true);
        const fileFormData = new FormData();
        fileFormData.append("userId", userId);

        if (profileImage && typeof profileImage !== "string") {
          fileFormData.append("profileImage", profileImage);
        }
        if (resume && typeof resume !== "string") {
          fileFormData.append("resume", resume);
        }

        if (coi && typeof coi !== "string") {
          fileFormData.append("insuranceCertificate", coi);
        }

        const { response: fileResponse, error: fileError } = await apiRequest({
          method: "PATCH",
          url: "uploadProfileImageAndResume",
          data: fileFormData,
          isToken: true,
        });

        if (fileError || fileResponse?.status !== 200) {
          console.error("Error uploading files:", fileError);
          dispatch({
            type: ALERT_DIALOG,
            payload: {
              open: true,
              title: "Warning",
              message:
                "Contractor created but files upload failed. You can upload files later from the profile.",
            },
          });
        }
        setFileUploadLoading(false);
      };

      // debugger;

      if (contractor?.addContractor?.step1?.previouslySubmittedForm) {
        // Check if files have changed
        const hasNewFiles =
          (profileImage && typeof profileImage !== "string") ||
          (resume && typeof resume !== "string") ||
          (coi && typeof coi !== "string");

        // Check if form values or files have changed
        const formChanged = !lodash.isEqual(
          values,
          contractor?.addContractor?.step1?.previouslySubmittedForm,
        );

        // 🔴 `PUT /editPersonalInformation` is the ONLY thing that flips
        // `isPersonalInformationAdded` (contractors.service#editPersonalInformation) and
        // fires `successCallbackStep1`. Skipping the request when nothing changed leaves
        // the flag false forever, so this wizard reopens on step 1 at every login and the
        // contractor can never finish onboarding — the same silent loop that had to be
        // fixed on steps 2, 3 and 4. Scoped to the contractor completing their own
        // profile: staff editing someone else keeps the "nothing changed" shortcut.
        const mustPersistOnboardingStep =
          isContractor &&
          !auth?.contractorDetails?.contractorProfile
            ?.isPersonalInformationAdded;

        if (!formChanged && !hasNewFiles && !mustPersistOnboardingStep) {
          // Nothing changed, just move to next step
          setCurrentStep((currentStep) => {
            return currentStep + 1;
          });
        } else if (!formChanged && hasNewFiles && !mustPersistOnboardingStep) {
          // Only files changed, upload files directly without updating other fields
          const userId = contractor?.addContractor?.step1?.data?._id;
          await uploadFilesIfNeeded(userId);
          setCurrentStep((currentStep) => {
            return currentStep + 1;
          });
        } else {
          dispatch({ type: ADD_CONTRACTOR_STEP1_UPDATE_REQUEST });
          // eslint-disable-next-line
          function updateContractorStep1() {
            dispatch(
              addContractorStep1UpdateAction({
                payload: finalPayload,
                formData: values,
                contractorId: contractor?.addContractor?.step1?.data?._id,
                successCallback: async ({ response }) => {
                  // Upload files after successful update
                  await uploadFilesIfNeeded(
                    response.data?.data?._id ||
                      contractor?.addContractor?.step1?.data?._id,
                  );

                  setCurrentStep((currentStep) => {
                    return currentStep + 1;
                  });

                  dispatch({
                    type: ADD_CONTRACTOR_STEP1_UPDATE_SUCCESS,
                    payload: {
                      data: response.data?.data,
                      formData: values,
                    },
                  });

                  if (successCallbackStep1) {
                    successCallbackStep1(response);
                  }
                },
                failureCallback: ({ error }) => {
                  dispatch({ type: ADD_CONTRACTOR_STEP1_UPDATE_FAILURE });
                  dispatch({
                    type: ALERT_DIALOG,
                    payload: {
                      open: true,
                      title: "Error!",
                      message:
                        error?.response?.data?.message || "Some error occured!",
                    },
                  });
                },
                afterResponseCallback: () => {},
              }),
            );
          }

          if (
            getEmailSanitized({
              email:
                contractor?.addContractor?.step1?.previouslySubmittedForm
                  ?.email,
            }) == "" &&
            isUserRole(
              USER_ROLES.SUPERADMIN,
              USER_ROLES.ADMIN,
              USER_ROLES.COORDINATOR,
            )
          ) {
            dispatch(
              addContractorEmailAction({
                payload: {
                  data: {
                    email: values?.email,
                  },
                  params: {
                    contractorId: contractor?.addContractor?.step1?.data?._id,
                  },
                },
                successCallback: ({ res }) => {
                  updateContractorStep1();
                  dispatch(
                    alertDialogAciton({
                      open: true,
                      title: "Success",
                      message: res?.message || "Contractor data updated",
                    }),
                  );
                },
              }),
            );
          } else {
            updateContractorStep1();
          }
        }
      } else {
        dispatch({ type: ADD_CONTRACTOR_STEP1_REQUEST });
        dispatch(
          addContractorStep1Action({
            sendToken: true,
            payload: finalPayload,
            formData: values,
            successCallback: async ({ response }) => {
              // addPersonalInformation is a bulk handler: returns { data: results[], errors[] }.
              // The modal creates a single contractor, so normalize to that created object.
              // Without this, data._id is undefined and the later steps call
              // addRoleInformation/undefined → 400 → the wizard loops on the roles page.
              const created = Array.isArray(response.data?.data)
                ? response.data.data[0]
                : response.data?.data;

              // The backend answers 200 even on failure (e.g. email already in use) and puts
              // the reason in errors[]. Surface it instead of silently looping.
              if (!created?._id) {
                const apiErr =
                  response.data?.errors?.[0]?.error ||
                  response.data?.message ||
                  "Could not save contractor. Please try again.";
                dispatch({ type: ADD_CONTRACTOR_STEP1_FAILURE });
                dispatch({
                  type: ALERT_DIALOG,
                  payload: { open: true, title: "Error!", message: apiErr },
                });
                return;
              }

              // Upload files after successful creation
              await uploadFilesIfNeeded(created._id);

              if (isSaveAndClose.current == true) {
                closeModalHandler();
                return;
              }
              setCurrentStep((currentStep) => {
                return currentStep + 1;
              });

              dispatch({
                type: ADD_CONTRACTOR_STEP1_SUCCESS,
                payload: {
                  data: created,
                  formData: values,
                },
              });

              if (successCallbackStep1) {
                successCallbackStep1(response);
              }
            },
            failureCallback: ({ error }) => {
              dispatch({ type: ADD_CONTRACTOR_STEP1_FAILURE });
              dispatch({
                type: ALERT_DIALOG,
                payload: {
                  open: true,
                  title: "Error!",
                  message: error?.response?.data?.message,
                },
              });
            },
            afterResponseCallback: () => {},
          }),
        );
      }
    },
  });

  const {
    values,
    setValues,
    setFieldValue,
    dirty,
    handleChange,
    handleBlur,
    touched,
    errors,
    submitForm,
    resetForm,
    validateForm,
    setTouched,
  } = formik;
  // Form schema...

  // 🔴 `age` is required by the schema but has NO input on the contractor view (it is
  // rendered only when `!allRequired`), and even there it is read-only — it is always
  // derived from the date of birth. The only writer used to be the date picker's
  // onChange, so an account whose document had no `age` (prefilled as "") failed
  // validation on a field with nowhere to show the error: "Save and Continue" did
  // nothing at all. Deriving it here keeps the value in sync no matter who is editing.
  const derivedAge = useMemo(() => {
    if (!values?.dateOfBirth) return null;

    const parsedDateOfBirth = dayjs(values.dateOfBirth);
    if (!parsedDateOfBirth.isValid()) return null;

    const years = dayjs().diff(parsedDateOfBirth, "year");
    return Number.isFinite(years) ? years : null;
  }, [values?.dateOfBirth]);

  useEffect(() => {
    // Compare as strings: legacy documents store the age as a string and we must not
    // loop by rewriting the same value with a different type.
    if (String(values?.age ?? "") === String(derivedAge ?? "")) return;

    setFieldValue("age", derivedAge);
  }, [derivedAge, values?.age, setFieldValue]);

  // Whoever said they have their own COI must upload it: every V.C., and a 1099 or W2
  // who answered Yes to the COI question (Tiffaney, 2026-10-12). Only the contractor is
  // held to it here: staff editing an older profile without a COI can still save other
  // fields, and the onboarding checklist keeps "COI upload" as a step either way.
  const coiRequired =
    isContractor &&
    (values?.contractorType === "V.C." ||
      contractor?.addContractor?.step1?.data?.workerTypeAnswers?.hasCoi === true);
  const [coiMissingError, setCoiMissingError] = useState(false);

  /**
   * Validates before submitting so a blocked form always says why.
   * Without this, formik's `submitForm()` swallows the failure and the button looks dead.
   */
  const saveAndContinueHandler = async ({ saveAndClose = false } = {}) => {
    const validationErrors = await validateForm();
    const coiMissing = coiRequired && !isDocumentAvailable(coi);
    setCoiMissingError(coiMissing);
    const incompleteFormMessage = buildIncompleteFormMessage({
      errors: coiMissing
        ? {
            ...validationErrors,
            insuranceCertificate: "Certificate Of Insurance is required",
          }
        : validationErrors,
      labels: STEP1_FIELD_LABELS,
    });

    if (incompleteFormMessage) {
      isSaveAndClose.current = false;
      // Mark every field with an error as touched so the inline messages show up too.
      setTouched(setNestedObjectValues(validationErrors, true), false);
      dispatch(
        alertDialogAciton({
          open: true,
          title: "Incomplete profile",
          message: incompleteFormMessage,
        }),
      );
      return;
    }

    isSaveAndClose.current = saveAndClose;
    submitForm();
  };

  // Handlers start...
  const handleMobileChange = (event) => {
    const input = event.target.value;
    const formattedInput = input
      .replace(/\D/g, "")
      .replace(/(\d{1,3})(\d{1,3})?(\d{1,4})?/, function (_, p1, p2, p3) {
        let formattedNumber = "";
        if (p1) {
          formattedNumber += p1;
        }
        if (p2) {
          formattedNumber += "-" + p2;
        }
        if (p3) {
          formattedNumber += "-" + p3;
        }
        return formattedNumber;
      });
    handleChange({
      target: {
        name: "mobile",
        value: formattedInput,
      },
    });
  };

  const getCitiesOfAState = (stateCode) => {
    const cities = City.getCitiesOfState(countryCode, stateCode);

    return cities?.map((city) => {
      return {
        label: city?.name,
        value: city?.name,
      };
    });
  };

  const imageClickHandler = useCallback(() => {
    if (imageRef.current) {
      imageRef.current.click();
    }
  }, []);

  const resumeClickHandler = useCallback(() => {
    if (resumeRef.current) {
      resumeRef.current.click();
    }
  }, []);

  const coiClickHandler = () => {
    if (coiRef.current) {
      coiRef.current.click();
    }
  };

  // const getStateByName = (stateName) => {
  //     const country = Country.getCountryByCode(countryCode);
  //     return State.getStatesOfCountry(country.isoCode)
  //     ?.filter(state => {
  //         return (state?.name === stateName)
  //     })
  //     ?.map(state => {
  //         return {
  //             label: state?.name,
  //             value: state?.isoCode,
  //         }
  //     })?.[0];
  // }
  // Handlers end...

  // useEffects start...
  React.useEffect(() => {
    if (contractor?.addContractor?.step1?.form) {
      setValues(contractor?.addContractor?.step1?.form);
      // const contractorType =
      //   contractor?.addContractor?.step1?.data?.contractorType;
      // const Is3rdparty =
      //   contractorType == CONTRACTOR_TYPE_3RD_PARTY.OUTSOURCED ||
      //   contractorType == CONTRACTOR_TYPE_3RD_PARTY.THIRD_PARTY;
      // setIs3rdParty(Is3rdparty);
      // if (Is3rdparty) {
      //   setOption(
      //     contractorType === CONTRACTOR_TYPE_3RD_PARTY.OUTSOURCED
      //       ? "own-company"
      //       : "work-individual",
      //   );
      // } else {
      //   setOption(
      //     contractorType === "V.C." ? "own-company" : "work-individual",
      //   );
      // }
      setInitialValues(contractor?.addContractor?.step1?.form);

      // Initialize file states from contractor data
      if (contractor?.addContractor?.step1?.data?.profileImage) {
        setProfileImage(contractor?.addContractor?.step1?.data?.profileImage);
      }
      if (contractor?.addContractor?.step1?.data?.thumbnail) {
        setThumbnail(contractor?.addContractor?.step1?.data?.thumbnail);
      }
      if (contractor?.addContractor?.step1?.data?.resume) {
        setResume(contractor?.addContractor?.step1?.data?.resume);
      }
      if (contractor?.addContractor?.step1?.data?.insuranceCertificate) {
        setCoi(contractor?.addContractor?.step1?.data?.insuranceCertificate);
      }
    }
  }, []);

  // React.useEffect(() => {
  //   if (dontRunOnMount?.resettingCity && changedFields?.state) {
  //     setFieldValue("city", "Select city");

  //     console.log(values, "cityUseEffect");
  //   }

  //   setDontRunOnMount((dontRunOnMount) => {
  //     return {
  //       ...dontRunOnMount,
  //       resettingCity: true,
  //     };
  //   });
  //   // isComponentMounted.current.resettingCity = true;
  // }, [values?.state, setFieldValue]);

  // React.useEffect(() => {
  //   if (dontRunOnMount?.updatingForm) {
  //     dispatch({
  //       type: SET_CONTRACTOR_MULTIPLE_FIELDS,
  //       payload: {
  //         addContractor: {
  //           ...contractor?.addContractor,
  //           step1: {
  //             ...contractor?.addContractor?.step1,
  //             // form: contractor.addContractor?.step1?.form ? (
  //             //     isValuesSyncedOnce ? values : contractor.addContractor?.step1?.form
  //             // ) : values,
  //             form: values,
  //           },
  //         },
  //       },
  //     });
  //   }
  //
  //   setDontRunOnMount((dontRunOnMount) => {
  //     return {
  //       ...dontRunOnMount,
  //       updatingForm: true,
  //     };
  //   });
  //   // setIsValuesSyncedOnce(true);
  //   // isComponentMounted.current.updatingForm = true;
  // }, [values, dispatch]);

  React.useEffect(() => {
    return () => {
      setDontRunOnMount({
        ...dontRunOnMountInit,
      });
    };
  }, []);

  // useEffect(() => {
  //   if (isUndefined(isThirdPartyParent)) return;
  //   setIs3rdParty(isThirdPartyParent);
  //   if (!isThirdPartyParent) {
  //     resetCompany();
  //   }
  // }, [isThirdPartyParent]);

  // useEffects end...

  const setCityAddress = useCallback(
    (address) => {
      let city,
        state,
        zipCode = "";

      for (let i = 0; i < address?.address_components?.length; i++) {
        if (address?.address_components?.[i]?.types?.includes("postal_code")) {
          zipCode = address?.address_components?.[i]?.long_name;
        } else if (
          address?.address_components?.[i]?.types?.includes(
            "administrative_area_level_1",
          )
        ) {
          state =
            address?.address_components?.[i]?.short_name ||
            address?.address_components?.[i]?.long_name;
        } else if (
          address?.address_components?.[i]?.types?.includes("locality")
        ) {
          city = address?.address_components?.[i]?.long_name;
        }
      }

      setFieldValue("city", city);
      setFieldValue("state", state);
      setFieldValue("zipCode", zipCode);
    },
    [setFieldValue],
  );

  // Mailing address search: the input keeps the street the person picked and the rest of
  // the address fills City, State and Zip Code. Only fills what Google returned.
  const setMailingAddress = useCallback(
    (address) => {
      const part = (type, short = false) => {
        const found = address?.address_components?.find((c) => c?.types?.includes(type));
        return (short ? found?.short_name : found?.long_name) || found?.long_name || "";
      };
      const street = [part("street_number"), part("route")].filter(Boolean).join(" ");
      const city = part("locality") || part("postal_town") || part("sublocality_level_1");
      const state = part("administrative_area_level_1", true);
      const zipCode = part("postal_code");
      if (street) setFieldValue("mailingAddress", street);
      if (city) setFieldValue("city", city);
      if (state) setFieldValue("state", state);
      if (zipCode) setFieldValue("zipCode", zipCode);
    },
    [setFieldValue],
  );

  const isDocumentAvailable = useCallback((doc) => {
    return doc && (typeof doc?.name == "string" || typeof doc == "string");
  }, []);

  function resetCompany() {
    setFieldValue("thirdPartyContractorId", null);
    setFieldValue("companyName", "");
  }

  const allRequired = useMemo(() => {
    return isContractor;
  }, [isContractor]);

  const showCompanyName = useMemo(() => {
    if (isContractor && values?.contractorType == "w2") {
      return auth?.isGustoApproved;
    }
    return ["w2", "V.C.", CONTRACTOR_TYPE_3RD_PARTY.THIRD_PARTY]?.includes(
      values?.contractorType,
    );
  }, [values?.contractorType, isContractor, auth?.isGustoApproved]);

  const showWorkMarketId = useMemo(() => {
    if (WORKMARKET_CONTRACTOR_TYPES?.includes(values?.contractorType)) {
      return true;
    } else return false;
  }, [values?.contractorType]);

  const companyNameRequired = useMemo(() => {
    return ["V.C.", CONTRACTOR_TYPE_3RD_PARTY.THIRD_PARTY]?.includes(
      values?.contractorType,
    );
  }, [values?.contractorType]);

  // Red label = required field. Mirrors the rules of commonSchema above; nothing in
  // view mode, where nothing can be edited.
  // A label stays red only while its field still needs work (empty or not valid yet),
  // so it turns normal as soon as it is filled correctly. With no field names, the
  // caller decides (e.g. the COI upload).
  const needsWork = (name) =>
    !String(values?.[name] ?? "").trim() || Boolean(errors?.[name]);
  const requiredSx = (isRequired, ...names) =>
    !isViewing && isRequired && (names.length === 0 || names.some(needsWork))
      ? requiredLabelSx
      : undefined;

  const isCompanyNameDisabled = useMemo(() => {
    return (
      isContractor &&
      values?.contractorType == "w2" &&
      ["california", "ca"]?.includes(values?.state?.trim()?.toLowerCase())
    );
  }, [values?.state, values?.contractorType, isContractor]);

  const checkIfUrlExist = useCallback(
    ({ contractorType }) => {
      const foundLink = links?.find((l) => l?.contractorType == contractorType);
      return foundLink ? true : false;
    },
    [links],
  );

  const hasLink = useMemo(() => {
    return checkIfUrlExist({
      contractorType: values?.contractorType || "NA",
    });
  }, [values?.contractorType, checkIfUrlExist]);

  const isTypeTbd = useMemo(() => {
    return values?.contractorType == "TBD" || values?.contractorType == "w2";
  }, [values?.contractorType]);

  useEffect(() => {
    if (isCompanyNameDisabled) {
      setFieldValue("companyName", "");
    }
    // eslint-disable-next-line
  }, [isCompanyNameDisabled]);

  useEffect(() => {
    return () => {
      if (resume && typeof resume?.name == "string")
        URL.revokeObjectURL(resume);
    };
  }, [resume]);

  useEffect(() => {
    return () => {
      if (coi && typeof coi?.name == "string") URL.revokeObjectURL(coi);
    };
  }, [coi]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* <Box className={addContractorModalStyles["formDataBox"]}> */}
      <FormDataBox
        style={{
          height: "80%",
          overflowY: "auto",
        }}
        className={"custom-scroll-design"}
      >
        {/* <Grid container spacing={2}
                    sx={{
                        height: '100%',
                        overflowY: 'auto'
                    }}
                    className={'custom-scroll-design'}
                > */}

        {isViewing ? null : <RequiredFieldsNote />}

        {/* Profile Picture Upload */}
        {isViewing && (thumbnail || profileImage) && (
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
            <Box sx={{ width: isSmallScreen ? "100%" : "50%" }}>
              <Grid item xs={12}>
                <Box className={addContractorModalStyles["form-box"]}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "1rem",
                      padding: "1rem",
                    }}
                  >
                    <Box
                      sx={{
                        flexShrink: 0,
                        "& img": {
                          borderRadius: "50%",
                          width: "150px",
                          height: "150px",
                          objectFit: "cover",
                          cursor: "pointer",
                          transition: "opacity 0.2s",
                          "&:hover": {
                            opacity: 0.7,
                          },
                        },
                      }}
                    >
                      {thumbnail || profileImage ? (
                        <img
                          src={
                            typeof profileImage === "string"
                              ? thumbnail || profileImage
                              : URL.createObjectURL(profileImage)
                          }
                          alt="Profile"
                          onClick={() => setImageViewModal(true)}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "80px",
                            height: "80px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: getCssVariableHandler({
                              variableName: "--dark-blue-1-rgb",
                            }),
                            border: `1px solid ${getCssVariableHandler({
                              variableName: "--font-rgb",
                            })}`,
                            borderRadius: "50%",
                          }}
                        >
                          <ImageIcon
                            style={{
                              color: getCssVariableHandler({
                                variableName: "--font-rgb",
                              }),
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Box>
          </Grid>
        )}

        {/* Resume Upload */}
        {isViewing && (resume || coi) && (
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
            <Box sx={{ width: isSmallScreen ? "100%" : "50%" }}>
              <Grid item xs={12}>
                <Box className={addContractorModalStyles["form-box"]}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "1rem",
                      padding: "1rem",
                    }}
                  >
                    <Box
                      sx={{
                        // flexShrink: 0,
                        width: "150px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: getCssVariableHandler({
                          variableName: "--dark-blue-1-rgb",
                        }),
                        border: `1px solid ${getCssVariableHandler({
                          variableName: "--font-rgb",
                        })}`,
                        borderRadius: "0.5rem",
                        cursor: isDocumentAvailable(resume)
                          ? "pointer"
                          : "default",
                        transition: "opacity 0.2s",
                        "&:hover": {
                          opacity: isDocumentAvailable(resume) ? 0.7 : 1,
                        },
                      }}
                      onClick={() => {
                        if (isDocumentAvailable(resume)) {
                          setResumeViewModal(true);
                        }
                      }}
                    >
                      {isDocumentAvailable(resume) ? (
                        <Typography>View Resume</Typography>
                      ) : (
                        <UploadFile
                          style={{
                            color: getCssVariableHandler({
                              variableName: "--font-rgb",
                            }),
                            fontSize: "2.5rem",
                          }}
                        />
                      )}
                    </Box>

                    {isDocumentAvailable(coi) && (
                      <Box
                        sx={{
                          // flexShrink: 0,
                          width: "150px",
                          height: "50px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: getCssVariableHandler({
                            variableName: "--dark-blue-1-rgb",
                          }),
                          border: `1px solid ${getCssVariableHandler({
                            variableName: "--font-rgb",
                          })}`,
                          borderRadius: "0.5rem",
                          cursor: isDocumentAvailable(coi)
                            ? "pointer"
                            : "default",
                          transition: "opacity 0.2s",
                          "&:hover": {
                            opacity: isDocumentAvailable(coi) ? 0.7 : 1,
                          },
                        }}
                        onClick={() => {
                          if (isDocumentAvailable(coi)) {
                            setCoiViewModal(true);
                          }
                        }}
                      >
                        {isDocumentAvailable(coi) ? (
                          <Typography>View COI</Typography>
                        ) : (
                          <UploadFile
                            style={{
                              color: getCssVariableHandler({
                                variableName: "--font-rgb",
                              }),
                              fontSize: "2.5rem",
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Box>
          </Grid>
        )}

        {isViewing &&
          !isContractor &&
          (isUserRole(USER_ROLES.SUPERADMIN) ||
            auth?.canViewCrewIdentityDocuments) && (
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
              <Box sx={{ width: isSmallScreen ? "100%" : "50%" }}>
                <GovernmentIdDocumentCard
                  mode="staff"
                  contractorId={contractor?.addContractor?.step1?.data?._id}
                  hideWhenMissing
                  compact
                />
              </Box>
            </Grid>
          )}

        <Grid item xs={isSmallScreen ? 12 : 4}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: "3px",
                ...requiredSx(true, "firstName", "lastName"),
              }}
            >
              Legal Name
              <InfoHover
                title={"Legal Name (should match with government issued id)"}
              />{" "}
            </InputLabel>
            <CustomInput
              fullWidth
              placeholder="Legal First Name"
              name="firstName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.firstName}
              readOnly={isViewing}
            />
            {touched.firstName && errors.firstName && (
              <span className={addContractorModalStyles.error}>
                {errors.firstName}
              </span>
            )}
          </Box>
        </Grid>
        <Grid item xs={isSmallScreen ? 12 : 4}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel
              sx={{
                visibility: "hidden",
              }}
            >
              MiddleName
            </InputLabel>
            <CustomInput
              fullWidth
              placeholder="Legal Middle Name (Optional)"
              name="middleName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.middleName}
              readOnly={isViewing}
            />
            {touched.middleName && errors.middleName && (
              <span className={addContractorModalStyles.error}>
                {errors.middleName}
              </span>
            )}
          </Box>
        </Grid>
        <Grid item xs={isSmallScreen ? 12 : 4}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel
              sx={{
                visibility: "hidden",
              }}
            >
              LastName
            </InputLabel>
            <CustomInput
              fullWidth
              placeholder="Legal Last Name"
              name="lastName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.lastName}
              readOnly={isViewing}
            />
            {touched.lastName && errors.lastName && (
              <span className={addContractorModalStyles.error}>
                {errors.lastName}
              </span>
            )}
          </Box>
        </Grid>
        {contractor?.addContractor?.step1?.previouslySubmittedForm ? (
          <Grid
            item
            xs={
              isSmallScreen
                ? 12
                : // : contractor?.addContractor?.step1?.previouslySubmittedForm &&
                  //     !isContractor
                  //   ? 3
                  !showWorkMarketId
                  ? 12
                  : 6
            }
          >
            <Box className={addContractorModalStyles["form-box"]}>
              <InputLabel>Contractor ID</InputLabel>
              <CustomInput
                fullWidth
                placeholder="Contractor ID"
                name="profileId"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.profileId}
                readOnly={
                  true
                  // isViewing ||
                  // ((auth?.userRole !== USER_ROLES.ADMIN || !auth?.canManageGusto) &&
                  //   auth?.userRole !== USER_ROLES.SUPERADMIN)
                }
              />
              {touched.profileId && errors.profileId && (
                <span className={addContractorModalStyles.error}>
                  {errors.profileId}
                </span>
              )}
            </Box>
          </Grid>
        ) : null}

        {showWorkMarketId ? (
          <Grid
            item
            xs={
              isSmallScreen
                ? 12
                : contractor?.addContractor?.step1?.previouslySubmittedForm
                  ? 6
                  : 12
            }
          >
            <Box className={addContractorModalStyles["form-box"]}>
              {/* Red like the required fields because onboarding needs it, but not
                  enforced here: the ID often comes later (Tiffaney, 2026-10-12). */}
              <InputLabel
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  ...requiredSx(isContractor, "workBookId"),
                }}
              >
                WorkMarket Id
                {isContractor && !isViewing ? (
                  <Link
                    href="https://www.workmarket.com"
                    target="_blank"
                    rel="noreferrer"
                    sx={{ fontSize: "0.8rem", color: "#00C8FF" }}
                  >
                    Get it at www.workmarket.com
                  </Link>
                ) : null}
              </InputLabel>
              <CustomInput
                fullWidth
                placeholder="Workmarket Id"
                name="workBookId"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.workBookId}
                readOnly={isViewing}
              />
              {touched.workBookId && errors.workBookId && (
                <span className={addContractorModalStyles.error}>
                  {errors.workBookId}
                </span>
              )}
              {isContractor && !isViewing && !values.workBookId ? (
                <Typography sx={{ fontSize: "0.8rem", opacity: 0.75, mt: 0.5 }}>
                  Needed to finish onboarding. You can add it later.
                </Typography>
              ) : null}
            </Box>
          </Grid>
        ) : null}

        <Grid item xs={12} md={6}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel>Preferred Name (optional)</InputLabel>
            <CustomInput
              fullWidth
              placeholder="Preferred Name"
              name="preferredName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.preferredName}
              readOnly={isViewing}
            />
            {touched.preferredName && errors.preferredName && (
              <span className={addContractorModalStyles.error}>
                {errors.preferredName}
              </span>
            )}
          </Box>
        </Grid>
        {isContractor ? null : (
          <Grid
            item
            xs={12}
            md={
              // contractor?.addContractor?.step1?.previouslySubmittedForm &&
              // !isContractor
              //   ? 3
              //   :
              6
            }
          >
            <InputLabel sx={requiredSx(true, "contractorType")}>Contractor Type</InputLabel>
            <Select
              fullWidth
              value={values?.contractorType || ""}
              displayEmpty
              onBlur={handleBlur}
              onChange={(e) => {
                if (
                  [
                    CONTRACTOR_TYPE_3RD_PARTY.THIRD_PARTY,
                    CONTRACTOR_TYPE_3RD_PARTY.OUTSOURCED,
                  ]?.includes(e?.target?.value)
                ) {
                  resetCompany();
                }
                formik.setFieldValue("contractorType", e?.target?.value);
              }}
              readOnly={
                (isViewing || isEditing) &&
                [
                  CONTRACTOR_TYPE_3RD_PARTY.THIRD_PARTY,
                  CONTRACTOR_TYPE_3RD_PARTY.OUTSOURCED,
                ]?.includes(
                  contractor?.addContractor?.step1?.previouslySubmittedForm
                    ?.contractorType,
                )
              }
              sx={{
                color: getCssVariableHandler({ variableName: "--white-rgb" }),
                border: `1px solid ${getCssVariableHandler({ variableName: "--white-rgb" })}`,
                borderRadius: "0.35rem",
                height: "50px",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiSvgIcon-root": {
                  color: getCssVariableHandler({
                    variableName: "--white-rgb",
                  }),
                },
              }}
              MenuProps={{
                sx: {
                  "& ul": {
                    overflowY: "auto",
                  },
                },
              }}
            >
              <MenuItem disabled value="" sx={{ display: "none" }}>
                <Typography sx={{ opacity: "0.6" }}>Contractor Type</Typography>
              </MenuItem>

              {contractorTypesOptions?.map((cont, i) => {
                return (
                  <MenuItem key={i} value={cont?.value}>
                    {cont?.label}
                  </MenuItem>
                );
              })}
            </Select>
            {touched.contractorType && errors.contractorType && (
              <span className={addContractorModalStyles.error}>
                {errors.contractorType}
              </span>
            )}
          </Grid>
        )}

        {showCompanyName ? (
          <Grid item xs={12} md={6}>
            <Box className={addContractorModalStyles["form-box"]}>
              <InputLabel sx={requiredSx(companyNameRequired, "companyName")}>
                Company Name {companyNameRequired ? "" : "(Optional)"}
              </InputLabel>
              <CustomInput
                fullWidth
                placeholder="Enter your business name"
                name="companyName"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.companyName}
                readOnly={isViewing}
              />
              {touched.companyName && errors.companyName && (
                <span className={addContractorModalStyles.error}>
                  {errors.companyName}
                </span>
              )}
            </Box>
          </Grid>
        ) : null}
        {values?.contractorType == CONTRACTOR_TYPE_3RD_PARTY?.OUTSOURCED ? (
          <Grid item xs={12}>
            <Box className={addContractorModalStyles["form-box"]}>
              <InputLabel>Company Name</InputLabel>
              <CustomBusinessInput
                readOnly={isViewing}
                onChange={(newVal) => {
                  formik.setFieldValue("companyName", newVal?.companyName);
                  formik.setFieldValue("thirdPartyContractorId", newVal?._id);
                }}
                handleBlur={handleBlur}
                value={
                  values?.thirdPartyContractorId
                    ? {
                        companyName: values?.companyName,
                        _id: values?.thirdPartyContractorId,
                      }
                    : null
                }
              />
              {touched.companyName && errors.companyName && (
                <span className={addContractorModalStyles.error}>
                  {errors.companyName}
                </span>
              )}
            </Box>
          </Grid>
        ) : null}

        {showCompanyName ? (
          <Grid item xs={12} md={6}>
            <Box className={addContractorModalStyles["form-box"]}>
              <InputLabel>EIN (Optional)</InputLabel>
              <CustomInput
                fullWidth
                placeholder="Enter your EIN"
                name="einNumber"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.einNumber}
                readOnly={isViewing}
              />
              {touched.einNumber && errors.einNumber && (
                <span className={addContractorModalStyles.error}>
                  {errors.einNumber}
                </span>
              )}
            </Box>
          </Grid>
        ) : null}

        <Grid item xs={isSmallScreen ? 12 : 6}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel sx={requiredSx(true, "mobile")}>Phone Number</InputLabel>
            <CustomInput
              fullWidth
              placeholder="XXX-XXX-XXXX"
              name="mobile"
              onChange={handleMobileChange}
              onBlur={handleBlur}
              value={values.mobile}
              readOnly={
                !isUserRole(USER_ROLES.SUPERADMIN, USER_ROLES.ADMIN) ||
                isViewing
              }
            />
            {touched.mobile && errors.mobile && (
              <span className={addContractorModalStyles.error}>
                {errors.mobile}
              </span>
            )}
          </Box>
        </Grid>
        <Grid item xs={isSmallScreen ? 12 : isContractor ? 12 : 6}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel sx={requiredSx(true, "email")}>Email</InputLabel>
            <CustomInput
              fullWidth
              placeholder="abc@xyz.com"
              name="email"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.email}
              readOnly={
                !isUserRole(USER_ROLES.SUPERADMIN, USER_ROLES.ADMIN) ||
                isViewing
              }
            />
            {touched.email && errors.email && (
              <span className={addContractorModalStyles.error}>
                {errors.email}
              </span>
            )}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel sx={requiredSx(isContractor, "mailingAddress")}>
              Mailing Address {!isContractor && `(Optional)`}
            </InputLabel>
            <AddressAutocomplete
              setAddress={setMailingAddress}
              placeholder="Start typing your street address"
              name="mailingAddress"
              onChange={handleChange}
              handleBlur={handleBlur}
              value={values.mailingAddress ?? ""}
              readOnly={isViewing}
            />
            {touched.mailingAddress && errors.mailingAddress && (
              <span className={addContractorModalStyles.error}>
                {errors.mailingAddress}
              </span>
            )}
          </Box>
        </Grid>
        <Grid item xs={isSmallScreen ? 12 : 4.5}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel sx={requiredSx(isContractor, "city")}>
              City {!isContractor && `(Optional)`}
            </InputLabel>
            <Box
              sx={{
                "& select": {
                  width: "100%",
                  border: "1px solid #fff",
                  borderRadius: "8px",
                  padding: "12px 12px",
                  background: "transparent",
                  color: "#fff",
                  fontSize: "16px",
                  "-webkit-appearance": "none",
                  appearance: "none",
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: "50px",
                  "& select": {
                    height: "100%",
                  },
                }}
              >
                {/* {isEditing || isViewing ? ( */}
                <AddressFilterAutocomplete
                  filterCities
                  filterStates
                  setAddress={setCityAddress}
                  fullWidth
                  placeholder="City"
                  name="city"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.city ?? ""}
                  readOnly={isViewing}
                />
                {/* ) : (
                  <>
                    <Select
                      name="city"
                      sx={{
                        border: `1px solid ${getCssVariableHandler({
                          variableName: "--font-rgb",
                        })}`,
                        borderRadius: "10px",
                        color: getCssVariableHandler({
                          variableName: "--font-rgb",
                        }),
                        "&:focus": {
                          outline: "none",
                          border: "none",
                        },
                        "&.MuiInputBase-root": {
                          width: "100%",
                          height: "100%",
                          "& .MuiSvgIcon-root": {
                            color: getCssVariableHandler({
                              variableName: "--font-rgb",
                            }),
                          },
                        },
                      }}
											value={
												values?.city ? values.city :
												values?.state
													? "Select city"
													: "Please select state first"
											}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
											<MenuItem
												key={"city-default"}
                        value={
                          values?.state
                            ? "Select city"
                            : "Please select state first"
                        }
                      >
                        {values?.state
                          ? "Select city"
                          : "Please select state first"}
                      </MenuItem>

											{values?.state
                        ? getCitiesOfAState(values?.state)?.map?.(
                            (cityOption) => {
                              return (
                                <MenuItem
                                  key={cityOption?._id}
                                  value={cityOption?.value}
                                >
                                  {cityOption?.label}
                                </MenuItem>
                              );
                            }
                          )
											: null}
                    </Select>
                  </>
                )} */}
              </Box>
              {touched.city && errors.city && (
                <span className={addContractorModalStyles.error}>
                  {errors.city}
                </span>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={isSmallScreen ? 12 : 4.5}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel sx={requiredSx(isContractor, "state")}>
              State {!isContractor && `(Optional)`}
            </InputLabel>
            <Box
              sx={{
                "& select": {
                  width: "100%",
                  border: "1px solid #fff",
                  borderRadius: "8px",
                  padding: "12px 12px",
                  background: "transparent",
                  color: "#fff",
                  fontSize: "16px",
                  "-webkit-appearance": "none",
                  appearance: "none",
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: "50px",
                  "& select": {
                    height: "100%",
                  },
                }}
              >
                {/* {isEditing || isViewing ? ( */}
                <CustomInput
                  fullWidth
                  placeholder="State"
                  name="state"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.state}
                  readOnly={isViewing}
                />
                {/* ) : (
                  <>
										<Select
                      name="state"
                      sx={{
                        border: `1px solid ${getCssVariableHandler({
                          variableName: '--font-rgb'
                        })}`, 
                        borderRadius: "10px",
                        color: getCssVariableHandler({
                          variableName: '--font-rgb'
                        }),
                        "&:focus": {
                          outline: 'none',
                          border: 'none'
                        },
                        "&.MuiInputBase-root": {
                          width: '100%',
                          height: '100%',
                          "& .MuiSvgIcon-root": {
                            color: getCssVariableHandler({
                              variableName: '--font-rgb'
                            }),
                          }
                        }
                      }}
                      value={values?.state || "Select state"}
											onChange={(e) => {
                        handleChange(e);
                        setChangedFields((changedFields) => {
                          return {
                            ...changedFields,
                            state: true,
                          };
                        });
                      }}
                      onBlur={handleBlur}
                    >
                      <MenuItem 
												value={"Select state"}
												key={"state-default"}
											>
                        {"Select state"}
                      </MenuItem>

                      {statesOptions?.map?.((state) => {
                        return (
                          <MenuItem
                            key={state?.value}
                            value={state?.value}
                          >
                            {state?.label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </>
                )} */}
              </Box>
              {touched.state && errors.state && (
                <span className={addContractorModalStyles.error}>
                  {errors.state}
                </span>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={isSmallScreen ? 12 : 3}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel sx={requiredSx(isContractor, "zipCode")}>
              Zip Code {!isContractor && `(Optional)`}
            </InputLabel>
            <CustomInput
              fullWidth
              placeholder="Zip"
              name="zipCode"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.zipCode}
              readOnly={isViewing}
            />
            {touched.zipCode && errors.zipCode && (
              <span className={addContractorModalStyles.error}>
                {errors.zipCode}
              </span>
            )}
          </Box>
        </Grid>

        {/* Date of Birth */}
        <Grid item xs={isSmallScreen ? 12 : allRequired ? 4 : 6}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel sx={requiredSx(isContractor, "dateOfBirth")}>
              Date of Birth {!isContractor && `(Optional)`}
            </InputLabel>
            <CustomDatePicker
              name="dateOfBirth"
              value={values.dateOfBirth ? dayjs(values.dateOfBirth) : null}
              onChange={(newValue) => {
                if (newValue === null) {
                  formik.setFieldValue("dateOfBirth", null);
                  formik.setFieldValue("age", null);
                } else if (newValue?.isValid()) {
                  formik.setFieldValue(
                    "dateOfBirth",
                    newValue ? newValue.format("YYYY-MM-DD") : "",
                  );
                  formik.setFieldValue("age", calculateAge(newValue));
                }
              }}
              readOnly={isViewing}
              disabled={isViewing}
              slotProps={{
                textField: {
                  onBlur: handleBlur,
                  error: touched.dateOfBirth && Boolean(errors.dateOfBirth),
                },
              }}
            />
            {touched.dateOfBirth && errors.dateOfBirth && (
              <span className={addContractorModalStyles.error}>
                {errors.dateOfBirth}
              </span>
            )}
          </Box>
        </Grid>

        {/* Age */}
        {!allRequired && (
          <Grid item xs={isSmallScreen ? 12 : 6}>
            <Box className={addContractorModalStyles["form-box"]}>
              <InputLabel>{allRequired ? "Age" : "Age (Optional)"}</InputLabel>
              <CustomInput
                fullWidth
                type="number"
                placeholder="Age"
                name="age"
                onChange={handleChange}
                onBlur={handleBlur}
                value={calculateAge(dayjs(values.dateOfBirth))}
                // readOnly={isViewing}
                readOnly
              />
              {touched.age && errors.age && (
                <span className={addContractorModalStyles.error}>
                  {errors.age}
                </span>
              )}
            </Box>
          </Grid>
        )}

        {/* Emergency Contact Name */}
        <Grid item xs={isSmallScreen ? 12 : allRequired ? 4 : 6}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel sx={requiredSx(isContractor, "emergencyContactName")}>
              Emergency Contact Name {!isContractor && `(Optional)`}
            </InputLabel>
            <CustomInput
              fullWidth
              placeholder="Emergency Contact Name"
              name="emergencyContactName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.emergencyContactName}
              readOnly={isViewing}
            />
            {touched.emergencyContactName && errors.emergencyContactName && (
              <span className={addContractorModalStyles.error}>
                {errors.emergencyContactName}
              </span>
            )}
          </Box>
        </Grid>

        {/* Emergency Contact Number */}
        <Grid item xs={isSmallScreen ? 12 : allRequired ? 4 : 6}>
          <Box className={addContractorModalStyles["form-box"]}>
            <InputLabel sx={requiredSx(isContractor, "emergencyContactNumber")}>
              Emergency Contact Number {!isContractor && `(Optional)`}
            </InputLabel>
            <CustomInput
              fullWidth
              placeholder="XXX-XXX-XXXX"
              name="emergencyContactNumber"
              onChange={(e) => {
                const input = e.target.value;
                const formattedInput = input
                  .replace(/\D/g, "")
                  .replace(
                    /(\d{1,3})(\d{1,3})?(\d{1,4})?/,
                    function (_, p1, p2, p3) {
                      let result = "";
                      if (p1) result += p1;
                      if (p2) result += "-" + p2;
                      if (p3) result += "-" + p3;
                      return result;
                    },
                  );
                handleChange({
                  target: {
                    name: "emergencyContactNumber",
                    value: formattedInput,
                  },
                });
              }}
              onBlur={handleBlur}
              value={values.emergencyContactNumber}
              readOnly={isViewing}
            />
            {touched.emergencyContactNumber &&
              errors.emergencyContactNumber && (
                <span className={addContractorModalStyles.error}>
                  {errors.emergencyContactNumber}
                </span>
              )}
          </Box>
        </Grid>

        {isSmallScreen ? (
          <Grid item xs={12}>
            <Box className={addContractorModalStyles["form-box"]}>
              <InputLabel>
                Preferred Local Airports {!isContractor && `(Optional)`}
              </InputLabel>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                  width: "100%",
                }}
              >
                {values?.preferredAirportIds?.map((item, idx) => {
                  const isLast =
                    idx === values?.preferredAirportIds?.length - 1;

                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        alignItems: "flex-end",
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                      }}
                    >
                      <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 60%" } }}>
                        <CustomAirportInput
                          onChange={(Id) => {
                            formik.setFieldValue(
                              "preferredAirportIds",
                              values?.preferredAirportIds?.map((air, i) =>
                                idx === i ? { ...air, Id } : air,
                              ),
                            );
                          }}
                          value={item?.Id}
                        />
                      </Box>

                      <Box
                        sx={{
                          flex: {
                            xs: "1 1 calc(100% - 60px)",
                            sm: "0 0 120px",
                          },
                        }}
                      >
                        <CustomInput
                          name="radius"
                          placeholder="Radius"
                          inputProps={{
                            step: 1,
                            onKeyDown: (e) => {
                              if (
                                e.key === "." ||
                                e.key === "," ||
                                e.key === "e"
                              ) {
                                e.preventDefault();
                              }
                            },
                            onInput: (e) => {
                              if (e.target.value > 999) e.target.value = 999;
                              if (e?.target?.value < 0) e.target.value = 0;
                            },
                          }}
                          defaultValue={50}
                          type="number"
                          value={item?.radius}
                          endAdornment={
                            <InputAdornment position="end">
                              <Typography>km</Typography>
                            </InputAdornment>
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              "preferredAirportIds",
                              values?.preferredAirportIds?.map((air, i) =>
                                idx === i
                                  ? { ...air, radius: e?.target?.value }
                                  : air,
                              ),
                            );
                          }}
                        />
                      </Box>

                      <Box sx={{ flex: "0 0 50px", height: "50px" }}>
                        <Tooltip
                          title={isLast ? "Add Airport" : "Delete Airport"}
                        >
                          <Button
                            size="small"
                            variant="text"
                            sx={{
                              backgroundColor: "#fff",
                              padding: "10px 10px",
                              width: "50px",
                              height: "50px",
                              minWidth: "auto",
                              ":hover": { backgroundColor: "#fff" },
                            }}
                            onClick={() => {
                              if (isLast) {
                                setFieldValue("preferredAirportIds", [
                                  ...values?.preferredAirportIds,
                                  { Id: {}, radius: 50 },
                                ]);
                              } else {
                                setFieldValue(
                                  "preferredAirportIds",
                                  values?.preferredAirportIds?.filter(
                                    (_, i) => idx !== i,
                                  ),
                                );
                              }
                            }}
                          >
                            {isLast ? (
                              <Add sx={{ fontSize: "1.25rem" }} />
                            ) : (
                              <img src={DeletePink} alt="" />
                            )}
                          </Button>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Grid>
        ) : (
          <>
            <Grid item xs={9}>
              <Box className={addContractorModalStyles["form-box"]}>
                <InputLabel>
                  Preferred Local Airports {!isContractor && `(Optional)`}
                </InputLabel>
                <Box
                  sx={{
                    "& select": {
                      width: "100%",
                      border: "1px solid #fff",
                      borderRadius: "8px",
                      padding: "12px 12px",
                      paddingTop: "0px !important",
                      background: "transparent",
                      color: "#fff",
                      fontSize: "16px",
                      "-webkit-appearance": "none",
                      appearance: "none",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      gap: "0.5rem",
                      width: "100%",
                      // height: "50px",
                      "& select": {
                        height: "100%",
                      },
                    }}
                  >
                    {values?.preferredAirportIds?.map((item, idx) => {
                      return (
                        <CustomAirportInput
                          key={idx}
                          onChange={(Id) => {
                            formik.setFieldValue(
                              "preferredAirportIds",
                              values?.preferredAirportIds?.map((air, i) => {
                                if (idx == i) {
                                  return { ...air, Id };
                                } else {
                                  return air;
                                }
                              }),
                            );
                          }}
                          value={item?.Id}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box className={addContractorModalStyles["form-box"]}>
                <InputLabel>Radius</InputLabel>
                <Box
                  sx={{
                    "& select": {
                      width: "100%",
                      border: "1px solid #fff",
                      borderRadius: "8px",
                      padding: "12px 12px",
                      paddingTop: "0px !important",
                      background: "transparent",
                      color: "#fff",
                      fontSize: "16px",
                      "-webkit-appearance": "none",
                      appearance: "none",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      gap: "0.5rem",
                      width: "100%",
                      // height: "50px",
                      "& select": {
                        height: "100%",
                      },
                    }}
                  >
                    {values?.preferredAirportIds?.map((item, idx) => {
                      return (
                        <CustomInput
                          key={idx}
                          name="radius"
                          placeholder="Enter Radius"
                          inputProps={{
                            step: 1,
                            onKeyDown: (e) => {
                              if (
                                e.key === "." ||
                                e.key === "," ||
                                e.key === "e"
                              ) {
                                e.preventDefault();
                              }
                            },
                            onInput: (e) => {
                              if (e.target.value > 999) e.target.value = 999;
                              if (e?.target?.value < 0) e.target.value = 0;
                            },
                          }}
                          defaultValue={50}
                          type="number"
                          value={item?.radius}
                          endAdornment={
                            <InputAdornment position="end">
                              <Typography>km</Typography>
                            </InputAdornment>
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              "preferredAirportIds",
                              values?.preferredAirportIds?.map((air, i) => {
                                if (idx == i) {
                                  return {
                                    ...air,
                                    radius: e?.target?.value,
                                  };
                                } else {
                                  return air;
                                }
                              }),
                            );
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-end",
                  flexDirection: "column",
                }}
              >
                <InputLabel></InputLabel>
                <InputLabel></InputLabel>
                <InputLabel></InputLabel>
                <InputLabel></InputLabel>
                <InputLabel></InputLabel>
                <InputLabel></InputLabel>
                <InputLabel></InputLabel>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    width: "100%",
                  }}
                >
                  {values?.preferredAirportIds?.map((item, idx) => {
                    if (idx == values?.preferredAirportIds?.length - 1) {
                      return (
                        <Box sx={{ height: "50px" }} key={idx}>
                          <Tooltip title={"Add Airport"}>
                            <Button
                              size="small"
                              variant="text"
                              sx={{
                                backgroundColor: "#fff",
                                padding: "10px 10px",
                                width: "auto",
                                minWidth: "auto",
                                ":hover": {
                                  backgroundColor: "#fff",
                                },
                              }}
                              onClick={() => {
                                // NOTE: do the add operation
                                setFieldValue("preferredAirportIds", [
                                  ...(values?.preferredAirportIds || {}),
                                  { Id: {}, radius: 50 },
                                ]);
                              }}
                            >
                              <Add sx={{ fontSize: "1.25rem" }} />
                            </Button>
                          </Tooltip>
                        </Box>
                      );
                    }
                    return (
                      <Box sx={{ height: "50px" }} key={idx}>
                        <Tooltip title={"Delete Airport"}>
                          <Button
                            size="small"
                            variant="text"
                            sx={{
                              backgroundColor: "#fff",
                              padding: "10px 10px",
                              width: "auto",
                              minWidth: "auto",
                              ":hover": {
                                backgroundColor: "#fff",
                              },
                            }}
                            onClick={() => {
                              // NOTE: do the delete operation
                              setFieldValue(
                                "preferredAirportIds",
                                values?.preferredAirportIds?.filter((_, i) => {
                                  return idx != i;
                                }),
                              );
                            }}
                          >
                            <img src={DeletePink} alt="" />
                          </Button>
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Grid>
          </>
        )}

        {touched.preferredAirportIds && errors.preferredAirportIds && (
          <span className={addContractorModalStyles.error}>
            {errors.preferredAirportIds}
          </span>
        )}

        {/* NOTE: additional airports  */}
        {isSmallScreen ? (
          <Grid item xs={12}>
            <Box className={addContractorModalStyles["form-box"]}>
              <InputLabel
                sx={{ display: "flex", gap: "0.25rem", alignItems: "center" }}
              >
                Additional Airports
                <Tooltip
                  placement="top"
                  title={
                    "If you can work in any other markets as a local please add additional airports"
                  }
                >
                  <Info sx={{ fontSize: "1.5rem" }} />
                </Tooltip>
              </InputLabel>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                  width: "100%",
                }}
              >
                {values?.additionalAirportIds?.map((item, idx) => {
                  const isLast =
                    idx === values?.additionalAirportIds?.length - 1;

                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        alignItems: "flex-end",
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                      }}
                    >
                      <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 60%" } }}>
                        <CustomAirportInput
                          onChange={(Id) => {
                            formik.setFieldValue(
                              "additionalAirportIds",
                              values?.additionalAirportIds?.map((air, i) =>
                                idx === i ? { ...air, Id } : air,
                              ),
                            );
                          }}
                          value={item?.Id}
                        />
                      </Box>

                      <Box
                        sx={{
                          flex: {
                            xs: "1 1 calc(100% - 60px)",
                            sm: "0 0 120px",
                          },
                        }}
                      >
                        <CustomInput
                          name="radius"
                          placeholder="Radius"
                          inputProps={{
                            step: 1,
                            onKeyDown: (e) => {
                              if (
                                e.key === "." ||
                                e.key === "," ||
                                e.key === "e"
                              ) {
                                e.preventDefault();
                              }
                            },
                            onInput: (e) => {
                              if (e.target.value > 999) e.target.value = 999;
                              if (e?.target?.value < 0) e.target.value = 0;
                            },
                          }}
                          defaultValue={50}
                          type="number"
                          value={item?.radius}
                          endAdornment={
                            <InputAdornment position="end">
                              <Typography>km</Typography>
                            </InputAdornment>
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              "additionalAirportIds",
                              values?.additionalAirportIds?.map((air, i) =>
                                idx === i
                                  ? { ...air, radius: e?.target?.value }
                                  : air,
                              ),
                            );
                          }}
                        />
                      </Box>

                      <Box sx={{ flex: "0 0 50px", height: "50px" }}>
                        <Tooltip
                          title={isLast ? "Add Airport" : "Delete Airport"}
                        >
                          <Button
                            size="small"
                            variant="text"
                            sx={{
                              backgroundColor: "#fff",
                              padding: "10px 10px",
                              width: "50px",
                              height: "50px",
                              minWidth: "auto",
                              ":hover": { backgroundColor: "#fff" },
                            }}
                            onClick={() => {
                              if (isLast) {
                                setFieldValue("additionalAirportIds", [
                                  ...values?.additionalAirportIds,
                                  { Id: {}, radius: 50 },
                                ]);
                              } else {
                                setFieldValue(
                                  "additionalAirportIds",
                                  values?.additionalAirportIds?.filter(
                                    (_, i) => idx !== i,
                                  ),
                                );
                              }
                            }}
                          >
                            {isLast ? (
                              <Add sx={{ fontSize: "1.25rem" }} />
                            ) : (
                              <img src={DeletePink} alt="" />
                            )}
                          </Button>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Grid>
        ) : (
          <>
            <Grid item xs={9}>
              <Box className={addContractorModalStyles["form-box"]}>
                <InputLabel sx={{ display: "flex" }}>
                  Additional Airports {!isContractor && `(Optional)`}
                  <Tooltip
                    placement="top"
                    title={
                      "If you can work in any other markets as a local please add additional airports"
                    }
                  >
                    <Info sx={{ fontSize: "1.5rem" }} />
                  </Tooltip>
                </InputLabel>
                <Box
                  sx={{
                    "& select": {
                      width: "100%",
                      border: "1px solid #fff",
                      borderRadius: "8px",
                      padding: "12px 12px",
                      paddingTop: "0px !important",
                      background: "transparent",
                      color: "#fff",
                      fontSize: "16px",
                      "-webkit-appearance": "none",
                      appearance: "none",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      gap: "0.5rem",
                      width: "100%",
                      // height: "50px",
                      "& select": {
                        height: "100%",
                      },
                    }}
                  >
                    {values?.additionalAirportIds?.map((item, idx) => {
                      return (
                        <CustomAirportInput
                          key={idx}
                          onChange={(Id) => {
                            formik.setFieldValue(
                              "additionalAirportIds",
                              values?.additionalAirportIds?.map((air, i) => {
                                if (idx == i) {
                                  return { ...air, Id };
                                } else {
                                  return air;
                                }
                              }),
                            );
                          }}
                          value={item?.Id}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box className={addContractorModalStyles["form-box"]}>
                <InputLabel>Radius</InputLabel>
                <Box
                  sx={{
                    "& select": {
                      width: "100%",
                      border: "1px solid #fff",
                      borderRadius: "8px",
                      padding: "12px 12px",
                      paddingTop: "0px !important",
                      background: "transparent",
                      color: "#fff",
                      fontSize: "16px",
                      "-webkit-appearance": "none",
                      appearance: "none",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      gap: "0.5rem",
                      width: "100%",
                      // height: "50px",
                      "& select": {
                        height: "100%",
                      },
                    }}
                  >
                    {values?.additionalAirportIds?.map((item, idx) => {
                      return (
                        <CustomInput
                          key={idx}
                          name="radius"
                          inputProps={{
                            step: 1,
                            onKeyDown: (e) => {
                              if (
                                e.key === "." ||
                                e.key === "," ||
                                e.key === "e"
                              ) {
                                e.preventDefault();
                              }
                            },
                            onInput: (e) => {
                              if (e.target.value > 999) e.target.value = 999;
                              if (e?.target?.value < 0) e.target.value = 0;
                            },
                          }}
                          placeholder="Enter Radius"
                          defaultValue={50}
                          type="number"
                          value={item?.radius}
                          endAdornment={
                            <InputAdornment position="end">
                              <Typography>km</Typography>
                            </InputAdornment>
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              "additionalAirportIds",
                              values?.additionalAirportIds?.map((air, i) => {
                                if (idx == i) {
                                  return {
                                    ...air,
                                    radius: e?.target?.value,
                                  };
                                } else {
                                  return air;
                                }
                              }),
                            );
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-end",
                  flexDirection: "column",
                }}
              >
                <InputLabel></InputLabel>
                <InputLabel></InputLabel>
                <InputLabel></InputLabel>
                <InputLabel></InputLabel>
                <InputLabel></InputLabel>
                <InputLabel></InputLabel>
                <InputLabel></InputLabel>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    width: "100%",
                  }}
                >
                  {values?.additionalAirportIds?.map((item, idx) => {
                    if (idx == values?.additionalAirportIds?.length - 1) {
                      return (
                        <Box sx={{ height: "50px" }} key={idx}>
                          <Tooltip title={"Add Airport"}>
                            <Button
                              size="small"
                              variant="text"
                              sx={{
                                backgroundColor: "#fff",
                                padding: "10px 10px",
                                width: "auto",
                                minWidth: "auto",
                                ":hover": {
                                  backgroundColor: "#fff",
                                },
                              }}
                              onClick={() => {
                                // NOTE: do the add operation
                                setFieldValue("additionalAirportIds", [
                                  ...(values?.additionalAirportIds || {}),
                                  { Id: {}, radius: 50 },
                                ]);
                              }}
                            >
                              <Add sx={{ fontSize: "1.25rem" }} />
                            </Button>
                          </Tooltip>
                        </Box>
                      );
                    }
                    return (
                      <Box sx={{ height: "50px" }} key={idx}>
                        <Tooltip title={"Delete Airport"}>
                          <Button
                            size="small"
                            variant="text"
                            sx={{
                              backgroundColor: "#fff",
                              padding: "10px 10px",
                              width: "auto",
                              minWidth: "auto",
                              ":hover": {
                                backgroundColor: "#fff",
                              },
                            }}
                            onClick={() => {
                              // NOTE: do the delete operation
                              setFieldValue(
                                "additionalAirportIds",
                                values?.additionalAirportIds?.filter((_, i) => {
                                  return idx != i;
                                }),
                              );
                            }}
                          >
                            <img src={DeletePink} alt="" />
                          </Button>
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Grid>
          </>
        )}

        {touched.additionalAirportIds && errors.additionalAirportIds && (
          <span className={addContractorModalStyles.error}>
            {errors.additionalAirportIds}
          </span>
        )}

        {/* Profile Picture Upload */}
        {!isViewing && (
          <Grid item xs={isSmallScreen ? 12 : 6}>
            <Box className={addContractorModalStyles["form-box"]}>
              <InputLabel>Profile Picture (Optional)</InputLabel>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "1rem",
                  padding: "1rem",
                  border: `1px solid ${getCssVariableHandler({
                    variableName: "--font-rgb",
                  })}`,
                  borderRadius: "0.5rem",
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    "& img": {
                      borderRadius: "50%",
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                      cursor: "pointer",
                      transition: "opacity 0.2s",
                      "&:hover": {
                        opacity: 0.7,
                      },
                    },
                  }}
                >
                  {thumbnail || profileImage ? (
                    <img
                      src={
                        typeof profileImage === "string"
                          ? thumbnail || profileImage
                          : URL.createObjectURL(profileImage)
                      }
                      alt="Profile"
                      onClick={() => setImageViewModal(true)}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "80px",
                        height: "80px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: getCssVariableHandler({
                          variableName: "--dark-blue-1-rgb",
                        }),
                        border: `1px solid ${getCssVariableHandler({
                          variableName: "--font-rgb",
                        })}`,
                        borderRadius: "50%",
                      }}
                    >
                      <ImageIcon
                        style={{
                          color: getCssVariableHandler({
                            variableName: "--font-rgb",
                          }),
                        }}
                      />
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) {
                        return;
                      }

                      if (
                        !validateFileType({
                          file,
                          allowedTypes: ["image/*"],
                          dispatch,
                        })
                      ) {
                        return;
                      }
                      setThumbnail(null); // Clear thumbnail when uploading new image
                      setTempProfileImage({
                        objectUrl: URL.createObjectURL(file),
                        blob: file,
                      });
                      setCropModal(true);
                    }}
                    ref={imageRef}
                    disabled={isViewing}
                  />
                  <Button
                    onClick={imageClickHandler}
                    disabled={isViewing}
                    sx={{
                      minWidth: "0",
                      padding: "0.5rem 2rem",
                    }}
                    className="btn-pink"
                  >
                    {thumbnail || profileImage ? "Update" : "Upload"}
                  </Button>
                  {!(thumbnail || profileImage) && (
                    <Typography
                      variant="body2"
                      sx={{
                        marginTop: "0.5rem",
                        color: getCssVariableHandler({
                          variableName: "--font-rgb",
                        }),
                        opacity: 0.7,
                      }}
                    >
                      Click to upload image
                    </Typography>
                  )}
                  {(thumbnail || profileImage) && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        marginTop: "0.5rem",
                        color: getCssVariableHandler({
                          variableName: "--font-rgb",
                        }),
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                    >
                      {typeof profileImage === "string" && thumbnail
                        ? "View profile picture"
                        : profileImage?.name}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        )}

        {/* Resume Upload */}
        {!isViewing && (
          <Grid item xs={isSmallScreen ? 12 : 6}>
            <Box className={addContractorModalStyles["form-box"]}>
              <InputLabel>Resume (Optional)</InputLabel>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "1rem",
                  padding: "1rem",
                  border: `1px solid ${getCssVariableHandler({
                    variableName: "--font-rgb",
                  })}`,
                  borderRadius: "0.5rem",
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: "80px",
                    height: "80px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: getCssVariableHandler({
                      variableName: "--dark-blue-1-rgb",
                    }),
                    border: `1px solid ${getCssVariableHandler({
                      variableName: "--font-rgb",
                    })}`,
                    borderRadius: "0.5rem",
                    cursor: isDocumentAvailable(resume) ? "pointer" : "default",
                    transition: "opacity 0.2s",
                    "&:hover": {
                      opacity: isDocumentAvailable(resume) ? 0.7 : 1,
                    },
                  }}
                  onClick={() => {
                    if (isDocumentAvailable(resume)) {
                      setResumeViewModal(true);
                    }
                  }}
                >
                  {isDocumentAvailable(resume) ? (
                    <Visibility
                      style={{
                        color: getCssVariableHandler({
                          variableName: "--font-rgb",
                        }),
                        fontSize: "2.5rem",
                      }}
                    />
                  ) : (
                    <UploadFile
                      style={{
                        color: getCssVariableHandler({
                          variableName: "--font-rgb",
                        }),
                        fontSize: "2.5rem",
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <input
                    type="file"
                    accept=".pdf"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      if (e.target.files.length > 0) {
                        const file = e.target.files[0];

                        if (
                          !validateFileType({
                            file,
                            allowedTypes: ["application/pdf"],
                            dispatch,
                          })
                        ) {
                          return;
                        }

                        if (file.type === "application/pdf") {
                          setResume(file);
                        } else {
                          dispatch({
                            type: ALERT_DIALOG,
                            payload: {
                              open: true,
                              title: "Invalid File",
                              message: "Please upload a PDF file only.",
                            },
                          });
                        }
                      }
                    }}
                    ref={resumeRef}
                    disabled={isViewing}
                  />
                  <Button
                    onClick={resumeClickHandler}
                    disabled={isViewing}
                    sx={{
                      minWidth: "0",
                      padding: "0.5rem 2rem",
                    }}
                    className="btn-pink"
                  >
                    {resume ? "Update" : "Upload"}
                  </Button>
                  {!resume && (
                    <Typography
                      variant="body2"
                      sx={{
                        marginTop: "0.5rem",
                        color: getCssVariableHandler({
                          variableName: "--font-rgb",
                        }),
                        opacity: 0.7,
                      }}
                    >
                      Click to upload PDF
                    </Typography>
                  )}
                  {resume && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        marginTop: "0.5rem",
                        color: getCssVariableHandler({
                          variableName: "--font-rgb",
                        }),
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                    >
                      {resume?.name || "View Resume"}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        )}

        {/* coi upload: required for a V.C. contractor, optional otherwise */}
        {!isViewing && (
          <Grid item xs={isSmallScreen ? 12 : 6}>
            <Box className={addContractorModalStyles["form-box"]}>
              <InputLabel sx={requiredSx(coiRequired && !isDocumentAvailable(coi))}>
                Certificate Of Insurance {coiRequired ? "" : "(Optional)"}
              </InputLabel>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "1rem",
                  padding: "1rem",
                  border: `1px solid ${getCssVariableHandler({
                    variableName: "--font-rgb",
                  })}`,
                  borderRadius: "0.5rem",
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: "80px",
                    height: "80px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: getCssVariableHandler({
                      variableName: "--dark-blue-1-rgb",
                    }),
                    border: `1px solid ${getCssVariableHandler({
                      variableName: "--font-rgb",
                    })}`,
                    borderRadius: "0.5rem",
                    cursor: isDocumentAvailable(coi) ? "pointer" : "default",
                    transition: "opacity 0.2s",
                    "&:hover": {
                      opacity: isDocumentAvailable(coi) ? 0.7 : 1,
                    },
                  }}
                  onClick={() => {
                    if (isDocumentAvailable(coi)) {
                      setCoiViewModal(true);
                    }
                  }}
                >
                  {isDocumentAvailable(coi) ? (
                    <Visibility
                      style={{
                        color: getCssVariableHandler({
                          variableName: "--font-rgb",
                        }),
                        fontSize: "2.5rem",
                      }}
                    />
                  ) : (
                    <UploadFile
                      style={{
                        color: getCssVariableHandler({
                          variableName: "--font-rgb",
                        }),
                        fontSize: "2.5rem",
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <input
                    type="file"
                    accept=".pdf"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      if (e.target.files.length > 0) {
                        const file = e.target.files[0];

                        if (
                          !validateFileType({
                            file,
                            allowedTypes: ["application/pdf"],
                            dispatch,
                          })
                        ) {
                          return;
                        }

                        if (file.type === "application/pdf") {
                          setCoi(file);
                        } else {
                          dispatch({
                            type: ALERT_DIALOG,
                            payload: {
                              open: true,
                              title: "Invalid File",
                              message: "Please upload a PDF file only.",
                            },
                          });
                        }
                      }
                    }}
                    ref={coiRef}
                  />
                  <Button
                    onClick={coiClickHandler}
                    sx={{
                      minWidth: "0",
                      padding: "0.5rem 2rem",
                    }}
                    className="btn-pink"
                  >
                    {coi ? "Update" : "Upload"}
                  </Button>
                  {!coi && (
                    <Typography
                      variant="body2"
                      sx={{
                        marginTop: "0.5rem",
                        color: getCssVariableHandler({
                          variableName: "--font-rgb",
                        }),
                        opacity: 0.7,
                      }}
                    >
                      Click to upload PDF
                    </Typography>
                  )}
                  {coi && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        marginTop: "0.5rem",
                        color: getCssVariableHandler({
                          variableName: "--font-rgb",
                        }),
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                    >
                      {coi?.name || "View COI"}
                    </Typography>
                  )}
                </Box>
              </Box>
              {coiMissingError && !isDocumentAvailable(coi) ? (
                <span className={addContractorModalStyles.error}>
                  Certificate Of Insurance is required
                </span>
              ) : null}
            </Box>
          </Grid>
        )}

        {!isViewing && isContractor && (
          <Grid item xs={isSmallScreen ? 12 : 6}>
            <Box className={addContractorModalStyles["form-box"]}>
              <GovernmentIdDocumentCard presentation="upload-tile" />
            </Box>
          </Grid>
        )}

        {!(isViewing || isEditing) &&
        false &&
        !contractor?.addContractor?.step1?.previouslySubmittedForm ? (
          <Grid item xs={12}>
            <Box className={addContractorModalStyles["form-box"]}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.25rem",
                }}
              >
                <Checkbox
                  onChange={(e) => {
                    // setTwoFactorAuthentication(e.target.checked);
                    setAgreedToTandC(e.target.checked);
                  }}
                  sx={{
                    color: getCssVariableHandler({
                      variableName: "--font-rgb",
                    }),
                    padding: "0",
                  }}
                  checked={agreedToTandC}
                />

                <Typography>
                  I accept the{" "}
                  <NavLink to={"/terms-and-conditions"} target="_blank">
                    Terms and Conditions
                  </NavLink>{" "}
                  and{" "}
                  <NavLink to={"/privacy-policy"} target="_blank">
                    Privacy policy
                  </NavLink>
                </Typography>
              </Box>
              {/* <Box className={individualFormStyles.profile}>
              <Box
                sx={{
                  "& img": {
                    borderRadius: "50%",
                  },
                }}
              >
                {values?.files ? (
                  <img
                    src={
                      typeof values?.files === "string" &&
                      values?.files?.includes?.("http")
                        ? values?.files
                        : URL.createObjectURL(values?.files)
                    }
                    alt={""}
                    style={{
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      padding: "2rem 3rem",
                      background: getCssVariableHandler({
                        variableName: "--dark-blue-1-rgb",
                      }),
                      border: `1px solid ${getCssVariableHandler({
                        variableName: "--font-rgb",
                      })}`,
                      borderRadius: "0.5rem",
                    }}
                  >
                    <ImageIcon
                      style={{
                        color: getCssVariableHandler({
                          variableName: "--font-rgb",
                        }),
                      }}
                    />
                  </Box>
                )}
              </Box>
              <Box>
                <input
                  type="file"
                  accept="image/*"
                  className={individualFormStyles.fileInput}
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      setFieldValue("files", e.target.files[0]);
                    }
                  }}
                  ref={imageRef}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                >
                  <Typography
                    variant="span"
                    sx={{
                      color: getCssVariableHandler({
                        variableName: "--font-rgb",
                      }),
                    }}
                  >
                    Upload Photo
                  </Typography>
                  <InputLabel htmlFor="upload-profile-picture">
                    <Button
                      onClick={imageClickHandler}
                      sx={{
                        minWidth: "0",
                        padding: "0.5rem 2rem",
                      }}
                    >
                      UPLOAD
                    </Button>
                  </InputLabel>
                </Box>
              </Box>
            </Box> */}
            </Box>
          </Grid>
        ) : null}
        {/* </Grid> */}
        {/* </Box> */}

        {(auth?.canManageGusto ||
          isUserRole(USER_ROLES.SUPERADMIN) ||
          auth?.addAdpLink) &&
          !isViewing &&
          ["1099", "V.C."]?.includes(values?.contractorType) && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.5rem",
                  mt: 3,
                  width: "100%",
                  ...(!hasLink
                    ? { opacity: "0.75", cursor: "not-allowed" }
                    : {}),
                }}
              >
                <Checkbox
                  name="sendemail"
                  sx={{
                    cursor: "pointer",
                    padding: "0",
                    color: getCssVariableHandler({
                      variableName: "--white-rgb",
                    }),
                    ...(!hasLink
                      ? { opacity: "0.75", cursor: "not-allowed" }
                      : {}),
                    "&.Mui-checked": {
                      color: getCssVariableHandler({
                        variableName: "--primary-pink-hex",
                      }),
                      "& svg": {
                        fill: getCssVariableHandler({
                          variableName: "--primary-pink-hex",
                        }),
                      },
                    },
                  }}
                  checked={!!sendEmail}
                  onChange={(e) => {
                    if (!hasLink) return;
                    setSendEmail(e?.target?.checked);
                  }}
                />
                <Typography>Send an onboarding link to contractor</Typography>
              </Box>
              {!hasLink && !isTypeTbd && (
                <Box
                  sx={{
                    display:
                      auth?.canManageGusto || isUserRole(USER_ROLES.SUPERADMIN)
                        ? "flex"
                        : "none",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: "1rem",
                    width: "100%",
                    paddingLeft: "1rem",
                  }}
                >
                  <Typography>
                    No onboarding link is currently set up for this contractor
                    type.{" "}
                    {auth?.addAdpLink ? (
                      <>
                        <a
                          className="email-link"
                          href="/configuration/contractor-links"
                          target="_blank"
                        >
                          Click here
                        </a>{" "}
                        to add.
                      </>
                    ) : (
                      "Please contact payroll to add/update links."
                    )}
                  </Typography>
                </Box>
              )}
            </>
          )}
      </FormDataBox>

      {uiController?.showBottomBtnBox ? (
        <Box
          className="bottom-btn-box"
          sx={{
            width: "100%",
            marginTop: "2rem",
            alignSelf: "flex-end",
          }}
        >
          <Button
            className="btn-black"
            sx={{
              width: "50%",
              height: "4.25rem",
              textTransform: "none !important",
            }}
            onClick={(e) => {
              // addContractorsDispatch({
              // type: 'RESET'
              // });
              // resetForm();
              closeModalHandler ? closeModalHandler() : null;
            }}
            // onClick={onClose}
          >
            <Typography className="btn-text">Close</Typography>
          </Button>

          <Button
            className="btn-pink"
            sx={{
              width: "50%",
              height: "4.25rem",
              textTransform: "none !important",
              "&:disabled": {
                background: "#d85ad8 !important",
              },
            }}
            onClick={(e) => {
              if (isViewing) {
                setCurrentStep((currentStep) => {
                  return currentStep + 1;
                });
                return;
              }

              if (
                agreedToTandC ||
                isViewing ||
                isEditing ||
                contractor?.addContractor?.step1?.previouslySubmittedForm
              ) {
                if (
                  isEditing &&
                  initialValues?.mobile !== values?.mobile &&
                  contractor?.addContractor?.step1?.data?.smsConsentStatusBySms
                ) {
                  dispatch(
                    alertDialogAciton({
                      open: true,
                      title: "Warning",
                      message:
                        "Changing your phone number clears your existing SMS consent for security reasons. You’ll need to provide consent again after the update.",
                      onOk: () => {
                        saveAndContinueHandler();
                      },
                      okLabel: "Update Profile",
                    }),
                  );

                  return;
                }
                saveAndContinueHandler();
              } else {
                dispatch({
                  type: ALERT_DIALOG,
                  payload: {
                    open: true,
                    title: "Alert!",
                    message:
                      "Please agree to terms and conditions and privacy policy to continue!",
                  },
                });
              }
            }}
            disabled={
              contractor?.addContractor?.step1?.submitFormRequestLoading ||
              fileUploadLoading
            }
            // onClick={onClose}
          >
            {(contractor?.addContractor?.step1?.submitFormRequestLoading &&
              !isSaveAndClose?.current) ||
            fileUploadLoading ? (
              <CircularProgress size={22} />
            ) : (
              <Typography className="btn-text">
                {isViewing ? "Continue" : "Save and Continue"}
              </Typography>
            )}
          </Button>

          {!isViewing && !isEditing && !isContractor && (
            <Button
              className="btn-pink"
              sx={{
                width: "50%",
                height: "4.25rem",
                textTransform: "none !important",
                "&:disabled": {
                  background: "#d85ad8 !important",
                },
              }}
              onClick={() => {
                if (
                  agreedToTandC ||
                  isViewing ||
                  isEditing ||
                  contractor?.addContractor?.step1?.previouslySubmittedForm
                ) {
                  if (
                    isEditing &&
                    initialValues?.mobile !== values?.mobile &&
                    contractor?.addContractor?.step1?.data
                      ?.smsConsentStatusBySms
                  ) {
                    dispatch(
                      alertDialogAciton({
                        open: true,
                        title: "Warning",
                        message:
                          "Changing your phone number clears your existing SMS consent for security reasons. You’ll need to provide consent again after the update.",
                        onOk: () => {
                          saveAndContinueHandler({ saveAndClose: true });
                        },
                        okLabel: "Update Profile",
                      }),
                    );

                    return;
                  }
                  saveAndContinueHandler({ saveAndClose: true });
                } else {
                  dispatch({
                    type: ALERT_DIALOG,
                    payload: {
                      open: true,
                      title: "Alert!",
                      message:
                        "Please agree to terms and conditions and privacy policy to continue!",
                    },
                  });
                }
              }}
              disabled={
                contractor?.addContractor?.step1?.submitFormRequestLoading ||
                fileUploadLoading
              }
              // onClick={onClose}
            >
              {(contractor?.addContractor?.step1?.submitFormRequestLoading &&
                isSaveAndClose?.current) ||
              fileUploadLoading ? (
                <CircularProgress size={22} />
              ) : (
                <Typography className="btn-text">{"Save and Close"}</Typography>
              )}
            </Button>
          )}
        </Box>
      ) : null}

      {/* Image Cropper Modal */}
      <ImageCropper
        open={cropModal}
        image={tempProfileImage}
        onComplete={(croppedImage) => {
          setCropModal(false);
          setProfileImage(croppedImage);
          setTempProfileImage({ objectUrl: "", blob: "" });
        }}
        containerStyle={{
          position: "relative",
          width: "100%",
          height: 300,
          background: "#333",
        }}
        onClose={() => {
          setCropModal(false);
          setTempProfileImage({ objectUrl: "", blob: "" });
        }}
      />

      {/* Resume Viewer Modal */}
      <Modal
        open={resumeViewModal}
        onClose={() => setResumeViewModal(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "90vw",
            height: "90vh",
            background: getCssVariableHandler({
              variableName: "--dark-blue-1-rgb",
            }),
            borderRadius: "0.5rem",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "1rem",
              borderBottom: `1px solid ${getCssVariableHandler({
                variableName: "--font-rgb",
              })}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: getCssVariableHandler({ variableName: "--font-rgb" }),
              }}
            >
              Resume Preview
            </Typography>
            <IconButton
              onClick={() => setResumeViewModal(false)}
              sx={{
                color: getCssVariableHandler({ variableName: "--font-rgb" }),
              }}
            >
              <Close />
            </IconButton>
          </Box>
          <iframe
            src={resume}
            style={{
              width: "100%",
              height: "calc(100% - 60px)",
              border: "none",
            }}
            title="Resume Preview"
          />
        </Box>
      </Modal>

      {/* coi view Modal */}
      <Modal
        open={coiViewModal}
        onClose={() => setCoiViewModal(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "90vw",
            height: "90vh",
            background: getCssVariableHandler({
              variableName: "--dark-blue-1-rgb",
            }),
            borderRadius: "0.5rem",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "1rem",
              borderBottom: `1px solid ${getCssVariableHandler({
                variableName: "--font-rgb",
              })}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: getCssVariableHandler({ variableName: "--font-rgb" }),
              }}
            >
              COI Preview
            </Typography>
            <IconButton
              onClick={() => setCoiViewModal(false)}
              sx={{
                color: getCssVariableHandler({ variableName: "--font-rgb" }),
              }}
            >
              <Close />
            </IconButton>
          </Box>
          <iframe
            src={
              !coi
                ? null
                : typeof coi !== "string"
                  ? URL?.createObjectURL(coi)
                  : coi
            }
            style={{
              width: "100%",
              height: "calc(100% - 60px)",
              border: "none",
            }}
            title="COI Preview"
          />
        </Box>
      </Modal>

      {/* Image Viewer Modal */}
      <Modal
        open={imageViewModal}
        onClose={() => setImageViewModal(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            background: getCssVariableHandler({
              variableName: "--dark-blue-1-rgb",
            }),
            borderRadius: "0.5rem",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "1rem",
              borderBottom: `1px solid ${getCssVariableHandler({
                variableName: "--font-rgb",
              })}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: getCssVariableHandler({ variableName: "--font-rgb" }),
              }}
            >
              Profile Picture
            </Typography>
            <IconButton
              onClick={() => setImageViewModal(false)}
              sx={{
                color: getCssVariableHandler({ variableName: "--font-rgb" }),
              }}
            >
              <Close />
            </IconButton>
          </Box>
          <Box
            sx={{
              padding: "2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {profileImage && (
              <img
                src={
                  typeof profileImage === "string"
                    ? profileImage
                    : URL.createObjectURL(profileImage)
                }
                alt="Profile Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </LocalizationProvider>
  );
};

AddContractorFormStep1.propTypes = {
  currentStep: PropTypes.number,
  setCurrentStep: PropTypes.func,
  closeModalHandler: PropTypes.func,
  uiController: PropTypes.object,
  isEditing: PropTypes.bool.isRequired,
  isViewing: PropTypes.bool.isRequired,
  successCallbackStep1: PropTypes.func,
};

export default AddContractorFormStep1;
